import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useInView } from "framer-motion";
import { 
  Flame, 
  Clock,
  Calendar,
  Award,
  ChevronRight,
  CheckCircle,
  Cookie,
  Cake,
  CakeSlice
} from "lucide-react";
import InquiryForm from "@/components/InquiryForm";

const candleImage = "https://images.pexels.com/photos/3270223/pexels-photo-3270223.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750";
const treatsImage = "https://customer-assets.emergentagent.com/job_luxury-facial-pro/artifacts/ubyn9wjm_FB_IMG_1775395400454.jpg";
const treatsImage2 = "https://customer-assets.emergentagent.com/job_luxury-facial-pro/artifacts/wi4wqhy3_FB_IMG_1775395381394.jpg";
const pastryImage = "https://customer-assets.emergentagent.com/job_luxury-facial-pro/artifacts/cu230l9g_FB_IMG_1775395340169.jpg";
const cakeImage = "https://images.pexels.com/photos/1721934/pexels-photo-1721934.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750";

const skillsCourses = [
  {
    id: "candle_making",
    title: "Candle Making Course",
    price: 50000,
    kitPrice: 15000,
    duration: "4 days (2 days per week)",
    icon: Flame,
    image: candleImage,
    description: "Learn the art of candle creation and gain hands-on experience in a refined, high-standard learning setting.",
    curriculum: [
      "Types of candle waxes and their uses",
      "Candle tools and proper measurements",
      "Where to source quality materials"
    ],
    creations: [
      "Pillar Candles",
      "Insect Repellent Candles",
      "Mason Jar Candles",
      "Bouquet Candles",
      "Scented Candles",
      "Massage Candles"
    ],
    schedule: "Monday – Friday: 10:30–12:30 | 2:30–4:30",
  },
  {
    id: "complete_treat",
    title: "Complete Treat Course",
    price: 52000,
    kitPrice: null,
    icon: Cookie,
    image: treatsImage,
    galleryImages: [treatsImage, treatsImage2],
    description: "Master the art of creating delicious decorated treats and desserts perfect for starting your own business.",
    curriculum: [
      "Treat preparation techniques",
      "Various treat recipes",
      "Decorating and presentation",
      "Character & themed designs",
      "Packaging for sale",
      "Business tips"
    ],
  },
  {
    id: "elementary_pastry",
    title: "Elementary Pastry Course",
    price: 25000,
    kitPrice: null,
    icon: CakeSlice,
    image: pastryImage,
    description: "Learn the fundamentals of pastry making - perfect for beginners looking to develop their baking skills.",
    curriculum: [
      "Basic pastry techniques",
      "Dough preparation",
      "Baking fundamentals",
      "Traditional pastry recipes",
      "Presentation skills"
    ],
  },
  {
    id: "cake_baking",
    title: "Complete Cake Baking & Decoration",
    price: 65000,
    kitPrice: null,
    icon: Cake,
    image: cakeImage,
    description: "Comprehensive course covering cake baking from scratch to professional decoration techniques.",
    curriculum: [
      "Cake baking fundamentals",
      "Different cake types and recipes",
      "Frosting and icing techniques",
      "Fondant work",
      "Cake decoration and design",
      "Professional finishing touches"
    ],
  },
];

const AnimatedSection = ({ children, className = "" }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 50 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
      transition={{ duration: 0.8 }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

export const SkillsAcademyPage = () => {
  const [expandedCourse, setExpandedCourse] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div data-testid="skills-academy-page" className="pt-20">
      {/* Hero Section */}
      <section className="py-24 md:py-32 bg-[#050505] relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div 
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url(${candleImage})` }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#050505] via-[#050505]/80 to-[#050505]" />
        </div>
        
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-3xl mx-auto"
          >
            <span className="font-body text-xs font-semibold tracking-[0.3em] uppercase text-[#C5A059]">
              Beyond Beauty
            </span>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-light text-white mt-4 mb-6">
              Skills<br />
              <span className="gold-gradient">Academy</span>
            </h1>
            <p className="font-body text-lg text-[#A3A3A3] leading-relaxed">
              Expand your skills beyond esthetics. Our Skills Academy offers hands-on 
              training in creative crafts, baking, and entrepreneurial skills to help you 
              start new ventures or explore new passions.
            </p>
          </motion.div>
        </div>
      </section>

      {/* All Courses Grid */}
      <section className="py-24 md:py-32 bg-[#0A0A0A]" data-testid="skills-courses-grid">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <AnimatedSection>
            <div className="text-center mb-16">
              <span className="font-body text-xs font-semibold tracking-[0.3em] uppercase text-[#C5A059]">
                Registration Now Open
              </span>
              <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-light text-white mt-4">
                Our Courses
              </h2>
              <div className="section-divider" />
            </div>
          </AnimatedSection>

          <div className="grid md:grid-cols-2 gap-8">
            {skillsCourses.map((course, index) => (
              <AnimatedSection key={course.id}>
                <div 
                  className={`bento-card overflow-hidden cursor-pointer transition-all duration-500 ${
                    expandedCourse === course.id ? 'border-[#C5A059]' : ''
                  }`}
                  onClick={() => setExpandedCourse(expandedCourse === course.id ? null : course.id)}
                  data-testid={`skills-course-${course.id}`}
                >
                  {/* Course Image */}
                  <div className="h-48 relative overflow-hidden">
                    <img 
                      src={course.image} 
                      alt={course.title}
                      className="w-full h-full object-cover transition-transform duration-700 hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#121212] to-transparent" />
                    <div className="absolute bottom-4 left-4 right-4 flex justify-between items-end">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 bg-[#121212] border border-[#C5A059] flex items-center justify-center">
                          <course.icon className="w-5 h-5 text-[#C5A059]" />
                        </div>
                      </div>
                      <div className="text-right">
                        <span className="font-heading text-2xl text-[#C5A059]">${course.price.toLocaleString()}</span>
                        <span className="font-body text-xs text-white block">GYD</span>
                      </div>
                    </div>
                  </div>

                  {/* Course Info */}
                  <div className="p-6">
                    <h3 className="font-heading text-xl text-white mb-2">{course.title}</h3>
                    <p className="font-body text-sm text-[#A3A3A3] mb-4">{course.description}</p>
                    
                    {course.kitPrice && (
                      <div className="flex items-center gap-2 text-sm text-[#A3A3A3] mb-4">
                        <span>Kit Fee: ${course.kitPrice.toLocaleString()} GYD</span>
                      </div>
                    )}

                    {course.duration && (
                      <div className="flex items-center gap-2 text-sm text-[#A3A3A3] mb-4">
                        <Clock size={14} className="text-[#C5A059]" />
                        <span>{course.duration}</span>
                      </div>
                    )}

                    {/* Expanded Content */}
                    {expandedCourse === course.id && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        className="border-t border-[#262626] pt-4 mt-4"
                      >
                        <h4 className="font-body text-xs tracking-wider uppercase text-[#C5A059] mb-3">
                          What You'll Learn
                        </h4>
                        <ul className="space-y-2 mb-4">
                          {course.curriculum.map((item, i) => (
                            <li key={i} className="flex items-center gap-2 text-[#A3A3A3] text-sm">
                              <ChevronRight size={12} className="text-[#C5A059]" />
                              {item}
                            </li>
                          ))}
                        </ul>

                        {course.creations && (
                          <>
                            <h4 className="font-body text-xs tracking-wider uppercase text-[#C5A059] mb-3">
                              You Will Create
                            </h4>
                            <div className="grid grid-cols-2 gap-2">
                              {course.creations.map((item, i) => (
                                <div key={i} className="flex items-center gap-2 text-[#A3A3A3] text-sm">
                                  <CheckCircle size={12} className="text-[#C5A059]" />
                                  {item}
                                </div>
                              ))}
                            </div>
                          </>
                        )}

                        {course.galleryImages && (
                          <div className="mt-4">
                            <h4 className="font-body text-xs tracking-wider uppercase text-[#C5A059] mb-3">
                              Sample Work
                            </h4>
                            <div className="grid grid-cols-2 gap-2">
                              {course.galleryImages.map((img, i) => (
                                <img 
                                  key={i}
                                  src={img} 
                                  alt={`${course.title} sample ${i + 1}`}
                                  className="w-full h-24 object-cover rounded"
                                />
                              ))}
                            </div>
                          </div>
                        )}

                        {course.schedule && (
                          <div className="mt-4 pt-4 border-t border-[#262626]">
                            <div className="flex items-center gap-2 text-sm text-[#A3A3A3]">
                              <Calendar size={14} className="text-[#C5A059]" />
                              <span>{course.schedule}</span>
                            </div>
                          </div>
                        )}
                      </motion.div>
                    )}

                    <div className="mt-4 pt-4 border-t border-[#262626]">
                      <span className="text-[#C5A059] text-sm">
                        {expandedCourse === course.id ? 'Click to collapse' : 'Click to see details'}
                      </span>
                    </div>
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* What's Included */}
      <section className="py-24 md:py-32 bg-[#050505]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <AnimatedSection>
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <span className="font-body text-xs font-semibold tracking-[0.3em] uppercase text-[#C5A059]">
                  Why Choose Us
                </span>
                <h2 className="font-heading text-3xl sm:text-4xl font-light text-white mt-4 mb-6">
                  Learn From Experience
                </h2>
                <p className="font-body text-[#A3A3A3] leading-relaxed mb-6">
                  Our Skills Academy courses are designed to give you practical skills 
                  you can use to start your own business or pursue a new passion. 
                  All courses include hands-on training with professional guidance.
                </p>
                <div className="space-y-4">
                  {[
                    "Hands-on practical training",
                    "All materials provided for in-class use",
                    "Small class sizes for personalized attention",
                    "Certificate upon completion",
                    "Business tips and guidance",
                  ].map((item, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <div className="w-2 h-2 bg-[#C5A059]" />
                      <span className="font-body text-[#A3A3A3]">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="bento-card p-8">
                <h3 className="font-body text-xs font-semibold tracking-[0.3em] uppercase text-[#C5A059] mb-6">
                  Course Pricing (GYD)
                </h3>
                <div className="space-y-4">
                  {skillsCourses.map((course) => (
                    <div key={course.id} className="flex justify-between items-center pb-4 border-b border-[#262626] last:border-0">
                      <div>
                        <span className="font-body text-white">{course.title}</span>
                        {course.kitPrice && (
                          <span className="block text-xs text-[#A3A3A3]">+ Kit: ${course.kitPrice.toLocaleString()}</span>
                        )}
                      </div>
                      <span className="font-heading text-xl text-[#C5A059]">${course.price.toLocaleString()}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Registration Section */}
      <section className="py-24 md:py-32 bg-[#0A0A0A]" data-testid="skills-registration">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <AnimatedSection>
              <div>
                <span className="font-body text-xs font-semibold tracking-[0.3em] uppercase text-[#C5A059]">
                  Starting April 2025
                </span>
                <h2 className="font-heading text-3xl sm:text-4xl font-light text-white mt-4 mb-6">
                  Register Now
                </h2>
                <p className="font-body text-[#A3A3A3] leading-relaxed mb-8">
                  Limited spaces available for all Skills Academy courses. Submit your 
                  inquiry and we'll contact you within 24 hours to secure your spot.
                </p>

                <div className="flex gap-4 mb-8">
                  <a 
                    href="https://www.facebook.com/share/p/1CpFMJ5mXp/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="border border-[#C5A059] text-[#C5A059] font-semibold text-sm tracking-[0.15em] uppercase px-6 py-3 hover:bg-[#C5A059]/10 transition-all duration-300 inline-flex items-center gap-2"
                  >
                    View on Facebook
                    <ChevronRight size={14} />
                  </a>
                </div>

                <div className="bento-card p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <Award className="w-6 h-6 text-[#C5A059]" />
                    <span className="font-body text-white">Certificate Awarded</span>
                  </div>
                  <p className="font-body text-sm text-[#A3A3A3]">
                    All Skills Academy courses include a certificate upon successful completion.
                  </p>
                </div>
              </div>
            </AnimatedSection>

            <AnimatedSection>
              <InquiryForm inquiryType="skills" defaultCourse="candle_making" />
            </AnimatedSection>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SkillsAcademyPage;
